import { ReactElement, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { PrimaryButton, SecondaryButton, SnackbarStatus, StatusBar } from '@weareopenr/component-library';
import { ITimetableDetail } from 'src/api/getTimetable';
import { useMenus } from 'src/hooks/useMenus';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { useTimetableRangesEnabled } from 'src/hooks/useTimetableRangesEnabled';
import { useTimetableMenuSets } from 'src/hooks/useTimetableMenuSets';
import { useUpdateTimetableDetails } from 'src/hooks/useUpdateTimetableDetails';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { filterMenusByRanges, filterMenuSetsByRanges, toDefaultMenuFields } from 'src/utils/timetables/helpers';
import { MenuSourceField } from 'src/components/CreateTimetableModal/fields/MenuSourceField';
import { TimetableDefaultsFields } from 'src/components/CreateTimetableModal/fields/TimetableDefaultsFields';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { ICloseTimetableDetailsTrayOptions, ITimetableDetailsFormValues, ITimetableDetailsPreview } from './types';
import styles from './TimetableDetailsTrayContent.module.scss';

interface TimetableDetailsFormProps {
  timetable: ITimetableDetail;
  onClose: (options?: ICloseTimetableDetailsTrayOptions) => void;
  // Called on every change to the previewable fields, so the timetable page behind the tray can
  // show a live preview of the pending edit - see AddScheduleTrayContent's onPreview for the
  // same pattern applied to adding a schedule. Optional: omitted where there's no page behind
  // the tray to preview against, e.g. the Timetables list page.
  onPreview?: (details: ITimetableDetailsPreview) => void;
}

export const TimetableDetailsForm = ({ timetable, onClose, onPreview }: TimetableDetailsFormProps): ReactElement => {
  const { mutateAsync: updateTimetableDetails } = useUpdateTimetableDetails(timetable.timetable_id);
  const { trackEvent, captureException } = useUserTracking();
  const { errorMessage, showNotification, clearError } = useModalNotification();

  const { data: allMenus = [], isLoading: isMenusLoading } = useMenus();
  const { data: allMenuSets, isLoading: isMenuSetsLoading, isError: isMenuSetsError } = useTimetableMenuSets();
  const { data: priceSchemes = [], isLoading: isPriceSchemesLoading } = usePriceSchemes();
  const { data: ranges = [], isLoading: isRangesLoading } = useRanges();
  const { isEnabled: rangesEnabled } = useTimetableRangesEnabled();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isSubmitting, isValid },
  } = useForm<ITimetableDetailsFormValues>({
    mode: 'onChange',
    defaultValues: {
      menuSource: timetable.ranges.length > 0 ? 'ranges' : 'all',
      rangeIds: timetable.ranges.map((range) => range.range_id),
      defaultMenuId: timetable.default_menu_set_id ?? timetable.default_menu_id ?? '',
      defaultPriceSchemeId: timetable.default_price_scheme_id,
    },
  });

  const menuSource = watch('menuSource');
  const rangeIds = watch('rangeIds');
  const defaultMenuId = watch('defaultMenuId');
  const defaultPriceSchemeId = watch('defaultPriceSchemeId');
  const isRangesSource = menuSource === 'ranges';

  // Menus aren't filtered server-side by range - filter the already-fetched list client-side,
  // the same way CreateTimetableModalContent narrows the default menu for a new timetable.
  const selectedRanges = useMemo(
    () => (isRangesSource ? ranges.filter((range) => rangeIds.includes(range.range_id)) : []),
    [isRangesSource, ranges, rangeIds],
  );
  const availableMenus = useMemo(
    () => (isRangesSource ? filterMenusByRanges(allMenus, selectedRanges) : allMenus),
    [isRangesSource, allMenus, selectedRanges],
  );
  const availableMenuSets = useMemo(
    () => (isRangesSource ? filterMenuSetsByRanges(allMenuSets, selectedRanges) : allMenuSets),
    [isRangesSource, allMenuSets, selectedRanges],
  );

  // If the current default menu (or menu set) falls outside the newly selected ranges, it's no
  // longer a valid choice and would otherwise sit selected but absent from the dropdown. Waits for
  // menu sets to load, and skips it if they failed to, so a menu set default isn't cleared before
  // it's known to be one.
  useEffect(() => {
    if (isMenuSetsLoading || isMenuSetsError) return;
    if (
      defaultMenuId &&
      !availableMenus.some((menu) => menu.menuId === defaultMenuId) &&
      !availableMenuSets.some((menuSet) => menuSet.menu_set_id === defaultMenuId)
    ) {
      setValue('defaultMenuId', '', { shouldValidate: true });
    }
  }, [availableMenus, availableMenuSets, isMenuSetsLoading, isMenuSetsError, defaultMenuId, setValue]);

  const isDefaultMenuSet = allMenuSets.some((menuSet) => menuSet.menu_set_id === defaultMenuId);

  const proposedRanges = useMemo(() => (isRangesSource ? selectedRanges : []), [isRangesSource, selectedRanges]);

  // Re-fires on every change to a previewable field, so the page behind the tray always reflects
  // the form's current values - `onPreview` is deliberately not a dep, it's stable from the
  // caller (Timetable.tsx), and including it would just re-run this on every render instead.
  useEffect(() => {
    onPreview?.({
      default_menu_id: isDefaultMenuSet ? null : defaultMenuId,
      default_menu_set_id: isDefaultMenuSet ? defaultMenuId : null,
      default_price_scheme_id: defaultPriceSchemeId,
      ranges: proposedRanges,
    });
  }, [defaultMenuId, isDefaultMenuSet, defaultPriceSchemeId, proposedRanges]);

  const onSubmit = async (values: ITimetableDetailsFormValues) => {
    clearError();

    try {
      await updateTimetableDetails({
        ...toDefaultMenuFields(values.defaultMenuId, allMenuSets),
        default_price_scheme_id: values.defaultPriceSchemeId,
        range_ids: values.menuSource === 'ranges' ? values.rangeIds : [],
      });

      trackEvent('timetable_details_updated', { timetableId: timetable.timetable_id });
      showNotification({ status: SnackbarStatus.SUCCESS, message: 'Timetable updated' });
      onClose({ updated: true });
    } catch (error) {
      captureException(error, { tags: { action: 'timetable_details_update' } });
      const message = error instanceof Error ? error.message : undefined;
      showNotification({
        status: SnackbarStatus.ERROR,
        message: message || 'Failed to update timetable. Please try again.',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['content']}>
      <div className={styles['fields']}>
        <MenuSourceField
          register={register}
          control={control}
          isRangesSource={isRangesSource}
          rangesEnabled={rangesEnabled}
          ranges={ranges}
          isRangesLoading={isRangesLoading}
        />

        <TimetableDefaultsFields
          control={control}
          menus={availableMenus}
          menuSets={availableMenuSets}
          isMenusLoading={isMenusLoading || isMenuSetsLoading}
          priceSchemes={priceSchemes}
          isPriceSchemesLoading={isPriceSchemesLoading}
          showDescription={false}
        />
      </div>

      {errorMessage && <StatusBar message={errorMessage} variant="error" />}

      <div className={styles['button-row']}>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <SecondaryButton buttonStyle="outline" type="button" size="full-width" onClick={() => onClose()}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton type="submit" size="full-width" loading={isSubmitting} disabled={isSubmitting || !isValid}>
              Save
            </PrimaryButton>
          }
        />
      </div>
    </form>
  );
};

export default TimetableDetailsForm;
